import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { getNames, getCode } from 'country-list';
import { FaEnvelopeOpenText, FaCheck, FaClock } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import API_URL from '../../config/api';

// دالة لعرض توست نجاح
const toastSuccess = (message) => {
  toast.success(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#000',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#000',
      secondary: '#fff',
    },
  });
};

// دالة لعرض توست خطأ
const toastError = (message) => {
  toast.error(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#dc2626',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#dc2626',
      secondary: '#fff',
    },
  });
};

const SignUp = () => {
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('signup-form'); 
  const navigate = useNavigate();

  const getAllCountries = () => {
    const countryNames = getNames();
    return countryNames
      .map(countryName => ({
        value: getCode(countryName),
        label: countryName
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const countries = [
    { value: '', label: 'Select country' },
    ...getAllCountries()
  ];

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required('First name is required')
      .min(2, 'First name must be at least 2 characters'),
    lastName: Yup.string()
      .required('Last name is required')
      .min(2, 'Last name must be at least 2 characters'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    country: Yup.string().required('Country is required'),
    city: Yup.string().required('City is required'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/^(?=.*[A-Za-z])(?=.*\d)/, 'Password must contain at least one letter and one number'),
    confirmPassword: Yup.string()
      .required('Please confirm your password')
      .oneOf([Yup.ref('password')], 'Passwords must match'),
    interest: Yup.string().required('Please select a collecting interest'),
    terms: Yup.boolean()
      .oneOf([true], 'You must accept the Terms of Service and Privacy Policy')
      .required('You must accept the Terms of Service and Privacy Policy')
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      city: '',
      password: '',
      confirmPassword: '',
      interest: '',
      terms: false,
      recommendations: false
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      
      const requestData = {
        firstname: values.firstName, 
        lastname: values.lastName, 
        email: values.email,
        country: values.country,
        city: values.city,
        password: values.password,
        primaryCollectingInterest: values.interest 
          ? values.interest.charAt(0).toUpperCase() + values.interest.slice(1) 
          : '',
        terms: values.terms,
        recommendations: values.recommendations
      };

      console.log('Request Data:', requestData);

      try {
        const response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });

        const data = await response.json();

        console.log('Status:', response.status);
        console.log('Response Data:', data);

        if (response.ok) {
          // عرض رسالة النجاح من الباك إند
          toastSuccess(data.message || 'Registration successful! Please check your email.');
          console.log('Registration successful:', data);
          setCurrentView('email-sent');
        } else {
          // عرض رسالة الخطأ من الباك إند
          toastError(data.message || 'Registration failed. Please try again.');
        }
      } catch (error) {
        console.error('Registration error:', error);
        toastError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    }
  });

  const resendVerificationEmail = async (email) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        // عرض رسالة النجاح من الباك إند
        toastSuccess(data.message || 'Verification email resent successfully');
        setCurrentView('resend-confirmation');
        return data;
      } else {
        // عرض رسالة الخطأ من الباك إند
        toastError(data.message || 'Failed to resend verification email');
        throw new Error(data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      toastError(error.message || 'Failed to resend email. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    try {
      await resendVerificationEmail(formik.values.email);
    } catch (error) {
      console.error('Failed to resend email:', error);
    }
  };

  const handleTryDifferentEmail = () => {
    setCurrentView('signup-form');
    formik.resetForm();
  };

  const interests = [
    { value: 'Contemporary Art', label: 'Contemporary Art' },
    { value: 'Modern Art', label: 'Modern Art' },
    { value: 'Photography', label: 'Photography' },
    { value: 'Sculpture', label: 'Sculpture' },
    { value: 'Mixed Media', label: 'Mixed Media' }
  ];

  // Success View - Email Sent
  if (currentView === 'email-sent') {
    return (
      <div className="flex min-h-screen">
        {/* Toaster Component */}
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              color: '#000',
              border: '1px solid #e5e5e5',
              padding: '16px',
              borderRadius: '0px',
              fontSize: '14px',
              fontWeight: 'normal',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
            success: {
              iconTheme: {
                primary: '#000',
                secondary: '#fff',
              },
            },
            error: {
              style: {
                background: '#fff',
                color: '#dc2626',
              },
              iconTheme: {
                primary: '#dc2626',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[440px]">
            <div className="mb-12">
              <div className="mb-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-8">
                  <FaEnvelopeOpenText className="text-black text-3xl" />
                </div>
                <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                  Verify Your Email
                </h2>
                <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                  We've sent a verification email to <span className="text-black font-medium">{formik.values.email}</span>
                </p>
              </div>
              <div className="space-y-6">
                <div className="p-6 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black mb-2 tracking-wide">
                        Check your inbox
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        Look for an email from ArtRewards with verification instructions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black mb-2 tracking-wide">
                        Click the verification link
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        The link will verify your email and activate your account
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                      3
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black mb-2 tracking-wide">
                        Sign in to your account
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        Once verified, sign in to start your collecting journey
                      </p>
                    </div>
                  </div>
                </div>
                <div className="text-center pt-6">
                  <p className="text-sm text-gray-600 font-normal tracking-wide mb-4">
                    Didn't receive the email?
                  </p>
                  <button 
                    onClick={handleResendEmail}
                    disabled={loading}
                    className="text-sm text-black hover:text-gray-600 transition-colors font-normal underline tracking-wide disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Resending...' : 'Resend verification email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'resend-confirmation') {
    return (
      <div className="flex min-h-screen">
        {/* Toaster Component */}
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              color: '#000',
              border: '1px solid #e5e5e5',
              padding: '16px',
              borderRadius: '0px',
              fontSize: '14px',
              fontWeight: 'normal',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            },
            success: {
              iconTheme: {
                primary: '#000',
                secondary: '#fff',
              },
            },
            error: {
              style: {
                background: '#fff',
                color: '#dc2626',
              },
              iconTheme: {
                primary: '#dc2626',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[440px]">
            <div className="mb-12">
              <div className="mb-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-black mb-8">
                  <FaCheck className="text-white text-3xl" />
                </div>
                <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                  Email Resent
                </h2>
                <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                  We've sent another verification email to <span className="text-black font-medium">{formik.values.email}</span>
                </p>
              </div>
              <div className="p-6 border border-gray-200 bg-gray-50">
                <div className="flex items-start space-x-4">
                  <FaClock className="text-black text-lg mt-1" />
                  <div>
                    <p className="text-sm font-medium text-black mb-3 tracking-wide">
                      Still Not Receiving Emails?
                    </p>
                    <ul className="text-sm text-gray-600 font-normal leading-relaxed tracking-wide space-y-2">
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Check your spam or junk folder</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Verify the email address is correct</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Add no-reply@artrewards.com to your contacts</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Contact our support team for assistance</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 space-y-4">
                <Link 
                  to="/support" 
                  className="block w-full bg-black text-white px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-700"
                >
                  Contact Support
                </Link>
                
                <button 
                  onClick={handleTryDifferentEmail}
                  className="block w-full border border-gray-300 bg-white text-gray-700 px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-50"
                >
                  Try Different Email
                </button>
                
                <div className="pt-4 text-center">
                  <Link 
                    to="/signin" 
                    className="text-sm text-gray-600 hover:text-black transition-colors font-normal tracking-wide underline"
                  >
                    Already verified? Sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Signup Form View
  return (
    <div className="flex min-h-screen">
      {/* Toaster Component */}
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#fff',
            color: '#000',
            border: '1px solid #e5e5e5',
            padding: '16px',
            borderRadius: '0px',
            fontSize: '14px',
            fontWeight: 'normal',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#000',
              secondary: '#fff',
            },
          },
          error: {
            style: {
              background: '#fff',
              color: '#dc2626',
            },
            iconTheme: {
              primary: '#dc2626',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 bg-white">
        <div className="w-full max-w-[440px]">
          <div className="mb-8 sm:mb-12">
            <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
              Create Account
            </h2>
            <p className="text-sm text-gray-600 font-normal tracking-wide">
              Begin your collecting journey
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-6 sm:space-y-8">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="w-full">
                  <label htmlFor="firstName" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    placeholder="First name"
                    className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                      formik.touched.firstName && formik.errors.firstName
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.firstName}</div>
                  )}
                </div>
                <div className="w-full">
                  <label htmlFor="lastName" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Last name"
                    className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                      formik.touched.lastName && formik.errors.lastName
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.lastName}</div>
                  )}
                </div>
              </div>
              <div className="mb-6 sm:mb-8">
                <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="your@email.com"
                  className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                    formik.touched.email && formik.errors.email
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-gray-300'
                  } focus:outline-none`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                )}
              </div>
            </div>

            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="w-full">
                  <label htmlFor="country" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    Country
                  </label>
                  <select
                    id="country"
                    name="country"
                    className={`w-full px-4 py-3 border text-sm font-normal bg-white transition-colors ${
                      formik.touched.country && formik.errors.country
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.country}
                  >
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                  {formik.touched.country && formik.errors.country && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.country}</div>
                  )}
                </div>
                <div className="w-full">
                  <label htmlFor="city" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="City"
                    className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                      formik.touched.city && formik.errors.city
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                  {formik.touched.city && formik.errors.city && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="w-full">
                  <label htmlFor="password" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="Create password"
                    className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                      formik.touched.password && formik.errors.password
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                  />
                  {formik.touched.password && formik.errors.password && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.password}</div>
                  )}
                </div>
                <div className="w-full">
                  <label htmlFor="confirmPassword" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm password"
                    className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                      formik.touched.confirmPassword && formik.errors.confirmPassword
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-gray-300'
                    } focus:outline-none`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmPassword}
                  />
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.confirmPassword}</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 font-normal tracking-wide">
                Minimum 8 characters with letters and numbers
              </p>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-4 font-medium">
                Primary Collecting Interest
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {interests.map((interest) => (
                  <label key={interest.value} className="flex items-center cursor-pointer group w-full">
                    <input
                      type="radio"
                      name="interest"
                      value={interest.value}
                      className="w-4 h-4 border-gray-300 text-black focus:ring-0 focus:ring-offset-0"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.interest === interest.value}
                    />
                    <span className="ml-2.5 text-sm text-gray-700 font-normal tracking-wide group-hover:text-black transition-colors">
                      {interest.label}
                    </span>
                  </label>
                ))}
              </div>
              {formik.touched.interest && formik.errors.interest && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.interest}</div>
              )}
            </div>

            <div className="pt-2">
              <div className="space-y-4">
                <label className="flex items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    name="terms"
                    className="w-4 h-4 border-gray-300 text-black focus:ring-0 focus:ring-offset-0 mt-0.5"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.terms}
                  />
                  <span className="ml-3 text-sm text-gray-700 font-normal leading-relaxed tracking-wide">
                    I agree to the{' '}
                    <Link to="/terms-of-service" target='_link' className="text-black hover:text-gray-600 transition-colors underline">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" target='_link' className="text-black hover:text-gray-600 transition-colors underline">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {formik.touched.terms && formik.errors.terms && (
                  <div className="text-red-500 text-xs ml-7 -mt-2">{formik.errors.terms}</div>
                )}
                
                <label className="flex items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    name="recommendations"
                    className="w-4 h-4 border-gray-300 text-black focus:ring-0 focus:ring-offset-0 mt-0.5"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.recommendations}
                  />
                  <span className="ml-3 text-sm text-gray-700 font-normal leading-relaxed tracking-wide">
                    I would like to receive personalized recommendations
                  </span>
                </label>
              </div>
            </div>

            <div className="pt-6 sm:pt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="mt-8 sm:mt-12 text-center">
            <p className="text-sm text-gray-600 font-normal tracking-wide">
              Already have an account?
              <Link to="/signin" className="text-black hover:text-gray-600 transition-colors font-normal ml-1 underline">
                Sign in
              </Link>
            </p>
            <p className="text-sm text-gray-600 font-normal tracking-wide mt-3">
              Are you an artist?
              <a href="#" className="text-black hover:text-gray-600 transition-colors font-normal ml-1 underline">
                Learn about artist applications
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;